import * as XLSX from 'xlsx';
import {TFunction} from 'i18next';
import moment from 'moment';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {User} from '../../../../backend/interfaces/user';
import {CareServiceItem} from '../../../../components/care-services/base/data/care-service-item';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {translations} from '../../../../i18n/translations';
import {scheduleReadableExpression} from '../../../functions/schedule-readable-expression';
import {transformCareServiceRepetitionToActivityTypeSchedule} from '../../../../components/care-services/base/data/care-service-data-transformation';
import type {PreparedXlsxExportFile} from '../base/export-data-types';
import {buildExportFileName} from '../base/export-file-name';

/**
 * Prepares an XLSX workbook for care services data export.
 */
export function prepareCareServicesXLSX({
  careServices,
  user,
  entity,
  usersData,
  t,
}: {
  careServices: CareServiceItem[];
  user?: User;
  entity?: EntityInfo;
  usersData: {seniors?: AffiliatedUser[]; caregivers?: AffiliatedUser[]};
  t: TFunction;
}): PreparedXlsxExportFile {
  const rows: string[][] = [];

  // ordry by date 1st
  const sortedCareServices = careServices.slice().sort((a, b) => {
    return a.startDate.valueOf() - b.startDate.valueOf();
  });

  // Build data rows
  for (const service of sortedCareServices) {
    // Find senior name
    const senior = usersData.seniors?.find((u) => u.user.id === service.targetUserId);
    const seniorName = senior?.user.displayName ?? senior?.user.name ?? service.targetUserId;

    // Get date strings
    const startDate = service.startDate.format('DD/MM/YYYY HH:mm');
    const endDate = service.endDate.format('DD/MM/YYYY HH:mm');

    // Get title
    const title = service.title ?? '';

    // Get caregiver names
    const caregiverNames =
      service.responsibleUserIds
        ?.map((id) => {
          const caregiver = usersData.caregivers?.find((u) => u.user.id === id);
          return caregiver?.user.displayName ?? caregiver?.user.name ?? id;
        })
        .join(', ') ?? '';

    // Get notes
    const notes = service.notes ?? '';

    // Format repetition
    const repetition = formatRepetition(service.repetition, t);

    rows.push([startDate, endDate, seniorName, title, caregiverNames, notes, repetition]);
  }

  // Column headers (translations)
  const seniorHeader = t(translations.care_services.form.senior_label);
  const startDateHeader = t(translations.care_services.form.start_date_label);
  const endDateHeader = t(translations.care_services.form.end_date_label);
  const titleHeader = t(translations.care_services.form.title_label);
  const caregiversHeader = t(translations.care_services.form.responsible_users_label);
  const notesHeader = t(translations.care_services.form.notes_label);
  const repetitionHeader = t(translations.care_services.form.repetition_label);

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  let worksheet = XLSX.utils.json_to_sheet(rows);

  // Add headers
  worksheet = XLSX.utils.sheet_add_aoa(worksheet, [
    [
      startDateHeader,
      endDateHeader,
      seniorHeader,
      titleHeader,
      caregiversHeader,
      notesHeader,
      repetitionHeader,
    ],
  ]);

  // Set column widths
  worksheet['!cols'] = [
    {wch: 20}, // Start Date
    {wch: 20}, // End Date
    {wch: 25}, // Senior Name
    {wch: 30}, // Title
    {wch: 30}, // Caregivers
    {wch: 40}, // Notes
    {wch: 40}, // Repetition
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Care Services');

  const fileName = buildExportFileName([
    user?.displayName ?? user?.name ?? entity?.shortName ?? entity?.name,
    user ? t(translations.seniorSideMenu.careServices) : t(translations.teamSideMenu.careServices),
  ]);

  return {
    file: workbook,
    fileName,
  };
}

/**
 * Formats repetition data into a human-readable string
 */
function formatRepetition(repetition: CareServiceItem['repetition'], t: TFunction): string {
  if (!repetition) return '';

  const parts: string[] = [];

  // Use existing schedule expression that already includes "every X day/week/etc"
  const scheduleDefinition = transformCareServiceRepetitionToActivityTypeSchedule(repetition);
  const expression = scheduleReadableExpression({
    scheduleDefinition: JSON.parse(scheduleDefinition),
    t,
  });

  parts.push(expression);

  // End condition
  if (repetition.endType === 'on-date' && repetition.endDate) {
    const endDate = moment(repetition.endDate).format('DD/MM/YYYY');
    parts.push(`${t(translations.care_services.export.repetition.ends_on)}: ${endDate}`);
  } else if (repetition.endType === 'after-occurrences' && repetition.endAfterOccurrencesValue) {
    parts.push(
      `${t(translations.care_services.export.repetition.ends_after)}: ${repetition.endAfterOccurrencesValue} ${t(translations.care_services.export.repetition.occurrences)}`
    );
  }

  return parts.join(' | ');
}
